import { createContext, useContext, type ReactNode } from "react";
import type { UsePartnerContextReturn } from "@/hooks/usePartnerContext";
import { usePartnerContextQuery } from "@/hooks/usePartnerContextQuery";

/**
 * React Context that carries the active partner identity (company,
 * activeTenant, capability map, entitlements, portal preferences …) across
 * every page rendered inside the partner shell.
 *
 * Pack 9.A consolidates the previously per-page hook calls into a single
 * fetch performed by `PartnerContextProvider`, so the Governance, Commercial,
 * Agreements and Verification History pages all read the SAME context object
 * — no risk of one page racing the other into a different `activeTenantId`.
 */
const PartnerCtx = createContext<UsePartnerContextReturn | null>(null);

/**
 * Provider wired into `PartnerLayout`. Children of the partner shell can
 * read the context via the `usePartnerContext()` hook, which transparently
 * falls back to a direct fetch when used outside the provider (e.g. embeds
 * in worker/wallet pages).
 */
export function PartnerContextProvider({ children }: { children: ReactNode }) {
  const value = usePartnerContextQuery();
  return <PartnerCtx.Provider value={value}>{children}</PartnerCtx.Provider>;
}

export function useOptionalPartnerContext(): UsePartnerContextReturn | null {
  return useContext(PartnerCtx);
}

export { PartnerCtx };
